import React from 'react';
import { Card } from '@/components/ui/Card';
import { FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

// Define day abbreviations
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface Schedule {
  ID: number;
  DeviceID: string;
  Index: number;
  Name: string;
  AuxType: number;
  AuxSlot: number;
  IsScheduleOn: boolean;
  StartTime: number;
  EndTime: number;
  WeekDay?: number;
  weekdays_array?: boolean[];
}

interface ScheduleCardProps {
  schedules: Schedule[];
  deviceAuxType: number;
}

// Convert minutes to formatted time (HH:MM)
const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const ScheduleCard: React.FC<ScheduleCardProps> = ({ schedules, deviceAuxType }) => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const [expandedScheduleId, setExpandedScheduleId] = React.useState<number | null>(null);
  
  const deviceSchedules = schedules.filter(schedule => schedule.AuxType === deviceAuxType);
  
  if (deviceSchedules.length === 0) {
    return null;
  }
  
  const toggleScheduleExpanded = (id: number) => {
    if (expandedScheduleId === id) {
      setExpandedScheduleId(null);
    } else {
      setExpandedScheduleId(id);
    }
  };
  
  return (
    <Card>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {deviceSchedules.map((schedule) => (
          <div 
            key={schedule.ID} 
            className={classNames(
              "border border-gray-200/50 dark:border-gray-700/30 rounded-lg overflow-hidden",
              "transition-all duration-200 hover:shadow-sm",
              {
                "bg-gray-50/70 dark:bg-gray-800/50": !schedule.IsScheduleOn,
                "bg-primary-50/70 dark:bg-primary-900/20": schedule.IsScheduleOn,
              }
            )}
          >
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <FiClock className="text-primary-500 text-base" />
                  <h3 className="font-medium text-base">{schedule.Name}</h3>
                </div>
                <div className={`px-1.5 py-0.5 rounded-full text-xs ${
                  schedule.IsScheduleOn
                    ? 'bg-green-100/80 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100/80 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300'
                }`}>
                  {schedule.IsScheduleOn ? 'Enabled' : 'Disabled'}
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Schedule Time</p>
                  <p className="text-sm font-medium">
                    {formatTime(schedule.StartTime)} - {formatTime(schedule.EndTime)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Active Days</p>
                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                    {schedule.weekdays_array && DAYS.map((day, index) => (
                      <div 
                        key={index}
                        className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                          schedule.weekdays_array![index]
                            ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default ScheduleCard; 